import { useState, Fragment } from 'react'
import { Check, ChevronDown, ChevronUp, CheckCircle2 } from 'lucide-react'
import type { LegacyPlanItem } from '@/utils/propostaPublicaV2Items'

function resolvePlanDescription(item: LegacyPlanItem) {
  return item.description || ''
}

function normalizeFeatureName(name: string) {
  const cleanName = name.includes(' ::: ') ? name.split(' ::: ')[1] : name
  return cleanName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .substring(0, 50)
}

export function getPlanCategory(item: LegacyPlanItem) {
  const name = (item.nome || item.name || '').toLowerCase()
  if (name.startsWith('combo')) return 'Combo'
  if (name.includes('marketing') || name.includes('mkt')) return 'Marketing'
  if (name.includes('rocket') || name.includes('busca')) return 'Rocket Search'
  if (name.includes('ecommerce') || name.includes('loja')) return 'E-commerce'
  if (name.includes('cro')) return 'CRO'
  return (item.nome || item.name || 'Outros').split(' ')[0]
}

type PlanCardProps = {
  item: LegacyPlanItem
  isSelected: boolean
  onToggle: () => void
}

export function PlanCard({ item, isSelected, onToggle }: PlanCardProps) {
  const [scopeOpen, setScopeOpen] = useState(false)
  const description = resolvePlanDescription(item)
  const planName = item.nome || item.name || 'Plano'
  const isRecurring = item.tipo === 'recorrente' || item.tipo === 'mensal'
  const itemPrice =
    isRecurring && item.pricing?.installmentValue
      ? Number(item.pricing.installmentValue)
      : Number(item.valor || item.pricing?.total || 0)

  const groupedCategories = (item.services || []).reduce<
    Record<string, Array<{ category: string; services: NonNullable<(typeof item.services)[number]['services']> }>>
  >((acc, cat) => {
    const parts = (cat.category || 'Geral').split(' ::: ')
    const planGroupName = parts[0]
    const subCatName = parts[1] || 'Geral'
    const filteredServices = (cat.services || []).filter((s) => s?.isIncluded !== false || s?.isPartial)
    if (filteredServices.length === 0) return acc
    if (!acc[planGroupName]) acc[planGroupName] = []
    acc[planGroupName].push({ category: subCatName, services: filteredServices })
    return acc
  }, {})

  return (
    <div className={`plan-card v2-card ${isSelected ? 'plan-card-selected' : ''}`}>
      <div className="plan-card-main">
        <div className="plan-card-info">
          <h3 className="plan-card-title">{planName}</h3>
          {description.split('\n').filter(Boolean).map((line, i) => (
            <p key={i} className="plan-card-desc">{line}</p>
          ))}
          {Object.keys(groupedCategories).length > 0 && (
            <button type="button" className="btn-toggle-scope" onClick={() => setScopeOpen((o) => !o)}>
              {scopeOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              {scopeOpen ? 'Ocultar escopo' : 'Ver escopo completo'}
            </button>
          )}
        </div>
        <div className="plan-card-pricing">
          <div className="plan-card-price">
            R$ {itemPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            {isRecurring && <span> / mês</span>}
          </div>
          <button type="button" className={`btn-select-plan ${isSelected ? 'btn-select-active' : ''}`} onClick={onToggle}>
            {isSelected ? (<><Check size={14} /> Selecionado</>) : 'Selecionar plano'}
          </button>
        </div>
      </div>

      {scopeOpen && Object.entries(groupedCategories).map(([planNameGroup, categories], pIdx) => (
        <div key={pIdx} className="plan-scope-expanded">
          <div className="plan-group-container">
            <h4 className="plan-group-title">{planNameGroup}</h4>
            <div className="scope-expanded-grid">
              {categories.map((cat, idx) => (
                <div key={idx} className="scope-expanded-card">
                  <h5>{cat.category}</h5>
                  <ul className="scope-list">
                    {cat.services.map((svc, sIdx) => (
                      <li key={sIdx}>
                        <Check size={13} color="#10b981" strokeWidth={3} />
                        <span>{svc?.name}{svc?.quantity ? <em> ({svc.quantity})</em> : null}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

type PlanFeature = { included: boolean; quantity: string | null }
type FeatureEntry = { displayName: string; category: string; perPlan: Array<PlanFeature | null> }

function CategoryComparison({
  items,
  onAccept,
}: {
  items: Array<LegacyPlanItem & { originalIdx: number }>
  onAccept: (idx: number) => void
}) {
  const featuredIdx = items.length - 1
  const featureMap = new Map<string, FeatureEntry>()

  items.forEach((item, planIdx) => {
    ;(item.services || []).forEach((cat) => {
      const subCategory = cat.category?.includes(' ::: ') ? cat.category.split(' ::: ')[1] : cat.category
      ;(cat.services || []).forEach((svc) => {
        const normKey = normalizeFeatureName(svc?.name || '')
        if (!normKey) return
        if (!featureMap.has(normKey)) {
          featureMap.set(normKey, {
            displayName: svc?.name || '',
            category: subCategory || 'Geral',
            perPlan: Array(items.length).fill(null),
          })
        }
        const entry = featureMap.get(normKey)!
        if (svc?.isIncluded !== false || svc?.isPartial) {
          entry.perPlan[planIdx] = { included: true, quantity: svc?.quantity || null }
        }
      })
    })
  })

  const categorized: Record<string, FeatureEntry[]> = {}
  featureMap.forEach((entry) => {
    if (!categorized[entry.category]) categorized[entry.category] = []
    categorized[entry.category].push(entry)
  })

  return (
    <div className="category-comparison-block" style={{ marginBottom: '3rem' }}>
      <div className="comparison-table-wrapper">
        <table className="comparison-table">
          <thead>
            <tr>
              <th className="feature-col" />
              {items.map((item, idx) => {
                const isFeatured = idx === featuredIdx
                const isRec = item.tipo === 'recorrente' || item.tipo === 'mensal'
                const displayValue = isRec && item.pricing?.installmentValue
                  ? Number(item.pricing.installmentValue)
                  : Number(item.valor || item.pricing?.total || 0)
                return (
                  <th key={idx} className={`plan-col plan-header-cell ${isFeatured ? 'featured-header' : ''}`}>
                    {isFeatured ? <div className="featured-badge">★ MAIS ESCOLHIDO</div> : <div className="badge-spacer" />}
                    <div className="plan-header-name">{item.nome || item.name}</div>
                    <div className="plan-header-price">
                      R$ {displayValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      {isRec && <small> /mês</small>}
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {Object.entries(categorized).map(([svcCat, features]) => (
              <Fragment key={svcCat}>
                <tr className="category-label-row">
                  <td colSpan={items.length + 1} className="category-label-cell">{svcCat}</td>
                </tr>
                {features.map((feat, fIdx) => (
                  <tr key={fIdx} className="feature-row">
                    <td className="feature-name-cell"><span className="feat-name">{feat.displayName}</span></td>
                    {feat.perPlan.map((plan, pIdx) => (
                      <td key={pIdx} className={`check-cell ${pIdx === featuredIdx ? 'featured-cell' : ''}`}>
                        {!plan?.included ? <span className="not-available">—</span> : (
                          <>
                            <CheckCircle2 size={18} color="#10b981" fill="#ecfdf5" />
                            {plan.quantity && <span className="feat-qty-inline">{plan.quantity}</span>}
                          </>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="cta-row">
              <td className="cta-empty" />
              {items.map((item, idx) => (
                <td key={idx} className={`cta-cell ${idx === featuredIdx ? 'featured-cell' : ''}`}>
                  <button
                    type="button"
                    className={`btn-cta-plan ${idx === featuredIdx ? 'btn-cta-featured' : 'btn-cta-secondary'}`}
                    onClick={() => onAccept(item.originalIdx)}
                  >
                    Quero este
                  </button>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

export function ComparisonTable({ items, onAccept }: { items: LegacyPlanItem[]; onAccept: (idx: number) => void }) {
  if (items.length < 2) return null
  const planCategories = items.reduce<Record<string, Array<LegacyPlanItem & { originalIdx: number }>>>(
    (acc, item, idx) => {
      const mainCat = getPlanCategory(item)
      if (!acc[mainCat]) acc[mainCat] = []
      acc[mainCat].push({ ...item, originalIdx: idx })
      return acc
    },
    {},
  )
  const normalizedCategories = Object.entries(planCategories).filter(([, catItems]) => catItems.length >= 2)
  if (normalizedCategories.length === 0) return null

  return (
    <div className="comparison-section">
      <h2 className="comparison-title">Comparativo dos Planos</h2>
      <p className="comparison-subtitle">Compare as features entre opções da mesma categoria</p>
      {normalizedCategories.map(([catName, catItems]) => (
        <CategoryComparison key={catName} items={catItems} onAccept={onAccept} />
      ))}
    </div>
  )
}
